import type { Tier } from "@/types/api";
import { request } from "./transport";

export interface LearningCounts {
  readonly id: string;
  readonly visits: number;
  readonly simulator_opened: number;
  readonly simulation_edited: number;
  readonly scenario_shared: number;
  readonly signup_completed: number;
  readonly strategy_saved: number;
}
export interface LearningReport {
  readonly days: number;
  readonly from: string;
  readonly to: string;
  readonly totals: LearningCounts;
  readonly sources: readonly LearningCounts[];
  readonly pages: readonly LearningCounts[];
}

export interface ManifoldCounts extends Omit<LearningCounts, "strategy_saved"> {
  readonly login_clicked: number;
}
export interface ManifoldReport extends Omit<
  LearningReport,
  "totals" | "sources" | "pages"
> {
  readonly totals: ManifoldCounts;
  readonly sources: readonly ManifoldCounts[];
  readonly pages: readonly ManifoldCounts[];
}

export interface SuperUserCounts {
  readonly total: number;
  readonly trial: number;
  readonly subscribed: number;
  readonly expired: number;
  readonly permanent: number;
  readonly admins: number;
}

export interface SuperPeriodCounts {
  readonly signups: number;
  readonly activated_signups: number;
  readonly pending_signups: number;
  readonly sign_ins: number;
  readonly payments: number;
  readonly revenue_cents: number;
  readonly backtests: number;
  readonly alerts_fired: number;
}

export interface SuperRecentSignIn {
  readonly id: number;
  readonly name: string;
  readonly email: string | null;
  readonly tier: Tier;
  readonly last_sign_in_at: string;
}

export interface SuperOverview {
  readonly users: SuperUserCounts;
  readonly today: SuperPeriodCounts;
  readonly week: SuperPeriodCounts;
  readonly recent_sign_ins: readonly SuperRecentSignIn[];
}

export interface SuperActivityPoint {
  readonly date: string;
  readonly signups: number;
  readonly sign_ins: number;
  readonly payments: number;
  readonly revenue_cents: number;
  readonly backtests: number;
}

export interface SuperActivity {
  readonly series: readonly SuperActivityPoint[];
  readonly dau: number;
  readonly wau: number;
  readonly mau: number;
}

export interface SuperEventActor {
  readonly id: number;
  readonly name: string;
}

export interface SuperEventTrackable {
  readonly type: string;
  readonly id: number;
}

export interface SuperEvent {
  readonly id: number;
  readonly category: string;
  readonly action: string;
  readonly user: SuperEventActor | null;
  readonly trackable: SuperEventTrackable | null;
  readonly data: Record<string, unknown> | null;
  readonly created_at: string;
}

export interface SuperEventsPage {
  readonly events: readonly SuperEvent[];
  readonly total: number;
  readonly page: number;
  readonly per_page: number;
}

export interface SuperEventWeek {
  readonly week: string;
  readonly count: number;
}

export interface SuperEventMeta {
  readonly pairs: readonly SuperEventPair[];
  readonly growth: readonly SuperEventWeek[];
  readonly total: number;
}

export interface SuperEventPair {
  readonly category: string;
  readonly action: string;
  readonly count: number;
}

export interface SuperEventFilter {
  readonly category?: string;
  readonly action?: string;
  readonly user_id?: number;
  readonly q?: string;
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  readonly per_page?: number;
}

export type SuperAdminSource = "column" | "env" | "both";

export interface SuperAdminRow {
  readonly id: number;
  readonly name: string;
  readonly email: string | null;
  readonly tier: Tier;
  readonly admin: boolean;
  readonly source: SuperAdminSource;
  readonly created_at: string;
}

export interface SuperGeoCountry {
  readonly iso: string;
  readonly name: string;
  readonly count: number;
}

export interface SuperGeoAttribution {
  readonly text: string;
  readonly url: string;
}

export interface SuperGeo {
  readonly countries: readonly SuperGeoCountry[];
  readonly total: number;
  readonly unresolved: number;
  readonly attribution: SuperGeoAttribution;
}

export interface SuperCohort {
  readonly week: string;
  readonly size: number;
  readonly returns: readonly (number | null)[];
}

export interface SuperCohorts {
  readonly cohorts: readonly SuperCohort[];
  readonly activation: {
    readonly new_users: number;
    readonly activated: number;
    readonly rate: number;
  };
}

export interface SuperActives {
  readonly series: readonly {
    readonly date: string;
    readonly actives: number;
    readonly rolling: number;
  }[];
  readonly dau: number;
  readonly wau: number;
  readonly mau: number;
  readonly stickiness: number;
}

export interface SuperGrowth {
  readonly series: readonly {
    readonly date: string;
    readonly signups: number;
    readonly total: number;
  }[];
}

export interface SuperTrendPoint {
  readonly date: string;
  readonly backtests: number;
  readonly strategies: number;
  readonly journal: number;
  readonly payments: number;
  readonly rolling_backtests: number;
  readonly rolling_strategies: number;
  readonly rolling_journal: number;
  readonly rolling_payments: number;
}

export interface ImpersonationResult {
  readonly id: number;
  readonly name: string;
}

function query(filter: SuperEventFilter): string {
  const qs = new URLSearchParams();
  if (filter.category) qs.set("category", filter.category);
  if (filter.action) qs.set("action_name", filter.action);
  if (filter.user_id != null) qs.set("user_id", String(filter.user_id));
  if (filter.q) qs.set("q", filter.q);
  if (filter.from) qs.set("from", filter.from);
  if (filter.to) qs.set("to", filter.to);
  if (filter.page != null) qs.set("page", String(filter.page));
  if (filter.per_page != null) qs.set("per_page", String(filter.per_page));
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export const superAdmin = {
  fetchManifoldReport: (days: number) =>
    request<ManifoldReport>(`/super/learning?scope=manifold&days=${days}`),
  fetchLearningReport: (days: number) =>
    request<LearningReport>(`/super/learning?days=${days}`),
  sendTelemetry: (events: readonly unknown[], unloading = false) =>
    request<void>("/telemetry", {
      body: JSON.stringify({ events }),
      keepalive: unloading,
      method: "POST",
    }).catch(() => undefined),

  fetchSuperOverview: () => request<SuperOverview>("/super/overview"),

  fetchSuperActivity: (days = 30) =>
    request<SuperActivity>(`/super/activity?days=${days}`),

  fetchSuperEvents: (filter: SuperEventFilter = {}) =>
    request<SuperEventsPage>(`/super/events${query(filter)}`),

  fetchSuperEventMeta: () => request<SuperEventMeta>("/super/events/meta"),

  fetchSuperGeo: (days = 30) => request<SuperGeo>(`/super/geo?days=${days}`),

  fetchCohorts: () => request<SuperCohorts>("/super/metrics/cohorts"),

  fetchActives: (days = 60) =>
    request<SuperActives>(`/super/metrics/actives?days=${days}`),

  fetchGrowth: () => request<SuperGrowth>("/super/metrics/growth"),

  fetchTrends: (days = 120) =>
    request<{ series: readonly SuperTrendPoint[] }>(
      `/super/metrics/trends?days=${days}`,
    ),

  fetchSuperAdmins: () =>
    request<{ admins: readonly SuperAdminRow[] }>("/super/admins"),

  grantAdmin: (userId: number) =>
    request<{ user: SuperAdminRow }>("/super/admins", {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    }),

  revokeAdminRole: (userId: number) =>
    request<{ user: SuperAdminRow }>(`/super/admins/${userId}`, {
      method: "DELETE",
    }),

  startImpersonation: (userId: number) =>
    request<ImpersonationResult>(`/super/impersonate/${userId}`, {
      method: "POST",
    }),

  stopImpersonation: () =>
    request<ImpersonationResult>("/super/impersonate", { method: "DELETE" }),
};
